import { processEnv } from 'components/configuration/environmentVariables'
import { useMemo } from 'react'

interface UseShouldHideWorkspaceToggleParams {
    firmCreatedDate?: string | null
}

/**
 * Determines if the workspace toggle should be hidden based on firm creation date.
 * Firms created on or after the cutoff date will have no option to switch to the old workspace.
 *
 * @param firmCreatedDate - The creation date of the firm (ISO 8601 format)
 * @returns true if the toggle should be hidden, false otherwise
 */
export const useShouldHideWorkspaceToggle = ({
    firmCreatedDate,
}: UseShouldHideWorkspaceToggleParams): boolean => {
    return useMemo(() => {
        // If no firm created date or no cutoff configured, show toggle
        const cutoffDate = processEnv.REACT_APP_NEW_WORKSPACE_CUTOFF_DATE

        if (!firmCreatedDate || !cutoffDate) {
            return false
        }

        try {
            const firmDate = new Date(firmCreatedDate)
            const cutoff = new Date(cutoffDate)

            // Validate dates
            if (isNaN(firmDate.getTime()) || isNaN(cutoff.getTime())) {
                // Invalid date format for workspace toggle logic
                return false
            }

            // Hide toggle if firm created on or after cutoff
            return firmDate >= cutoff
        } catch (error) {
            return false // Fail open - show toggle if dates invalid
        }
    }, [firmCreatedDate])
}